import React, { useState, useEffect, useMemo } from "react";
import { postData, getData } from "../../services/FetchNodeservices";
import { useNavigate } from "react-router-dom";
import { PieChart, Pie, Cell, ResponsiveContainer } from "recharts";

/* ================================================= */
/* TEAM LEADS CONFIG */
/* ================================================= */
const TEAM_LEADS = [
  59, 26, 36, 19, 22, 16, 39, 63, 48, 54, 77, 21, 44, 69, 27, 23, 5, 29, 130, 54, 133
];

export default function Home() {
  const navigate = useNavigate();

  // User details
  const user = useMemo(() => {
    return JSON.parse(localStorage.getItem("USER")) || {};
  }, []);

  const userId = user?.id || null;

  // State values
  const [tasks, setTasks] = useState([]);
  const [teamMembers, setTeamMembers] = useState([]);
  const [joinings, setJoinings] = useState([]);
  const [interviews, setInterviews] = useState([]);
  const [loading, setLoading] = useState(true);

  // Selected calendar date (defaults to today's date in YYYY-MM-DD format)
  const [selectedDateStr, setSelectedDateStr] = useState(() => {
    const d = new Date();
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  });

  // Tab state for onboarding widget
  const [onboardingTab, setOnboardingTab] = useState("new");

  // Fetch Dashboard Database Data
  const loadDashboardData = async () => {
    if (!userId) return;
    try {
      // 1. Fetch Working Tasks for user/team
      const tasksRes = await postData("workingtask/fetch", { user_id: userId });
      if (tasksRes?.status) {
        setTasks(tasksRes.data || []);
      }

      // 2. Fetch direct reports team members list
      const teamRes = await getData(`team-structure/team/${userId}`);
      if (teamRes?.status) {
        setTeamMembers(teamRes.team || []);
      }

      // 3. Fetch placements / joinings
      const joinRes = await getData(`placementtracker/fetch/${userId}`);
      if (joinRes?.status) {
        setJoinings(joinRes.data || []);
      }

      // 4. Fetch Interview Pipeline data
      const interviewRes = await postData("interview/fetch", { user_id: userId });
      if (interviewRes?.status) {
        setInterviews(interviewRes.data || []);
      }
    } catch (err) {
      console.error("Dashboard Fetch Error:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadDashboardData();
  }, [userId]);

  // Greeting helper
  const greeting = useMemo(() => {
    const hr = new Date().getHours();
    if (hr < 12) return "Good morning";
    if (hr < 17) return "Good afternoon";
    return "Good evening";
  }, []);

  // Generate Week Days (capsule calendar)
  const weekDays = useMemo(() => {
    const days = [];
    const startOfWeek = new Date();
    const currentDay = startOfWeek.getDay();
    const distanceToMonday = currentDay === 0 ? -6 : 1 - currentDay;
    startOfWeek.setDate(startOfWeek.getDate() + distanceToMonday);

    const weekdayNames = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];

    for (let i = 0; i < 7; i++) {
      const date = new Date(startOfWeek);
      date.setDate(startOfWeek.getDate() + i);
      const dNum = date.getDate();
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const day = String(date.getDate()).padStart(2, "0");
      const fullDateStr = `${year}-${month}-${day}`;

      days.push({
        name: weekdayNames[i],
        shortName: weekdayNames[i].substring(0, 3),
        dayNum: dNum,
        fullDateStr,
        isToday: date.toDateString() === new Date().toDateString(),
      });
    }
    return days;
  }, []);

  // Format selected date to dd-mm-yyyy for display
  const formattedSelectedDate = useMemo(() => {
    if (!selectedDateStr) return "";
    const parts = selectedDateStr.split("-");
    if (parts.length !== 3) return selectedDateStr;
    return `${parts[2]}-${parts[1]}-${parts[0]}`;
  }, [selectedDateStr]);

  // Filter Tasks by selected Calendar date
  const activeTasks = useMemo(() => {
    return tasks.filter(t => t.date === selectedDateStr);
  }, [tasks, selectedDateStr]);

  // Summary Metrics calculations based on selected date
  const metrics = useMemo(() => {
    const completed = activeTasks.filter(t => t.status === "Completed").length;
    const inProgress = activeTasks.filter(t => t.status === "In Progress" || t.status === "Pending").length;
    const total = activeTasks.length;
    const incomplete = activeTasks.filter(t => t.status === "Expired").length;

    const progressPercent = total > 0 ? Math.round((completed / total) * 100) : 0;

    return { completed, inProgress, total, incomplete, progressPercent };
  }, [activeTasks]);

  // Recharts Donut data
  const donutData = useMemo(() => {
    return [
      { name: "Progress", value: metrics.progressPercent, color: "#6366f1" },
      { name: "Remaining", value: 100 - metrics.progressPercent, color: "#f4f6fa" }
    ];
  }, [metrics.progressPercent]);

  // Kanban task lists based on selected date
  const kanbanColumns = useMemo(() => {
    const columns = {
      todo: [],
      inProgress: [],
      completed: [],
      incomplete: [],
    };

    activeTasks.forEach(t => {
      const status = (t.status || "").toLowerCase();
      if (status === "completed") {
        columns.completed.push(t);
      } else if (status === "in progress") {
        columns.inProgress.push(t);
      } else if (status === "expired") {
        columns.incomplete.push(t);
      } else {
        columns.todo.push(t);
      }
    });

    return columns;
  }, [activeTasks]);

  // Team members mapping (Real Data)
  const displayTeam = useMemo(() => {
    return teamMembers.map((m, idx) => ({
      name: m.name,
      designation: m.designation || "Recruiter",
      office: m.office || "N/A",
      color: idx % 3 === 0 ? "bg-orange-500" : idx % 3 === 1 ? "bg-indigo-500" : "bg-emerald-500"
    }));
  }, [teamMembers]);

  // Filter Joinings (Onboarding widget)
  const displayJoinings = useMemo(() => {
    const sorted = [...joinings].sort((a, b) => new Date(a.doj) - new Date(b.doj));
    if (onboardingTab === "new") {
      // Future joinings
      return sorted.filter(j => new Date(j.doj) >= new Date()).slice(0, 2);
    }
    // Completed / past joinings
    return sorted.filter(j => new Date(j.doj) < new Date()).slice(0, 2);
  }, [joinings, onboardingTab]);

  // Normalize interview dates safely
  const normalizeDate = (dateValue) => {
    if (!dateValue) return null;
    if (/^\d{4}-\d{2}-\d{2}$/.test(dateValue)) return dateValue;
    const d = new Date(dateValue);
    if (isNaN(d)) return null;
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  // Filter upcoming interviews
  const upcomingInterviews = useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    return interviews
      .filter(item => {
        const iDateStr = normalizeDate(item.interview_date);
        if (!iDateStr) return false;
        const iDate = new Date(iDateStr);
        iDate.setHours(0, 0, 0, 0);
        return iDate >= today;
      })
      .sort((a, b) => new Date(a.interview_date) - new Date(b.interview_date))
      .slice(0, 3);
  }, [interviews]);

  return (
    <div className="space-y-6">
      {/* 2-COLUMN MAIN & RIGHT LAYOUT (Matches reference image grid) */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 items-start">
        
        {/* ================================================= */}
        {/* LEFT COLUMN: MAIN CONTENT (70% width) */}
        {/* ================================================= */}
        <div className="lg:col-span-2 space-y-6">
          
          {/* Greeting Banner */}
          <div className="bg-white border border-gray-100 rounded-3xl p-6 shadow-sm flex flex-col md:flex-row md:items-center justify-between gap-4">
            <div>
              <h2 className="text-xl md:text-2xl font-black text-gray-900 leading-tight">
                Hello, {user?.first_name || "Employee"}! Schedule for {formattedSelectedDate}
              </h2>
              <p className="text-sm font-semibold text-gray-400 mt-1">
                You have {metrics.total - metrics.completed} pending tasks on this date
              </p>
            </div>
            {/* Greeting Progress bar */}
            <div className="w-full md:w-64 space-y-2">
              <div className="flex justify-between text-xs font-bold text-gray-500">
                <span>Task progress</span>
                <span>{metrics.progressPercent}%</span>
              </div>
              <div className="w-full h-2 bg-[#f4f6fa] rounded-full overflow-hidden">
                <div
                  className="h-full bg-orange-400 rounded-full transition-all duration-500"
                  style={{ width: `${metrics.progressPercent}%` }}
                />
              </div>
            </div>
          </div>

          {/* Calendar / Schedule Capsule Widgets */}
          <div className="bg-white border border-gray-100 rounded-3xl p-6 shadow-sm space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="text-base font-black text-gray-800">Calendar / Schedule</h3>
              <button className="p-1.5 text-gray-400 hover:text-gray-600 rounded-xl hover:bg-gray-50">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
                </svg>
              </button>
            </div>

            {/* Horizontal Capsule Grid */}
            <div className="grid grid-cols-7 gap-3">
              {weekDays.map((day, idx) => {
                const active = selectedDateStr === day.fullDateStr;
                return (
                  <button
                    key={idx}
                    onClick={() => setSelectedDateStr(day.fullDateStr)}
                    className={`flex flex-col items-center justify-center py-4 rounded-3xl transition duration-200 border border-gray-50/50 ${
                      active
                        ? "bg-indigo-600 text-white shadow-xl shadow-indigo-200 font-bold scale-105"
                        : "bg-[#f8f9fc] text-gray-700 hover:bg-gray-100"
                    }`}
                  >
                    <span className={`text-[10px] uppercase font-bold tracking-wider ${active ? "text-indigo-100" : "text-gray-400"}`}>
                      {day.shortName}
                    </span>
                    <span className="text-base font-black mt-2">
                      {day.dayNum}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* 4 Summary Stat Cards Grid */}
          <div className="space-y-4">
            <h3 className="text-base font-black text-gray-800">Summary</h3>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
              
              {/* Card 1: Completed */}
              <div className="bg-[#eafaf1] rounded-3xl p-4 shadow-sm flex items-center gap-4 hover:shadow-md transition">
                <div className="w-10 h-10 rounded-2xl bg-white flex items-center justify-center text-[#22c55e] font-bold text-lg shadow-sm">
                  ✓
                </div>
                <div>
                  <p className="text-[10px] font-bold text-[#22c55e]/80 uppercase tracking-wide">Completed</p>
                  <p className="text-base font-black text-gray-800 leading-tight">{metrics.completed} Tasks</p>
                </div>
              </div>

              {/* Card 2: In Progress */}
              <div className="bg-[#f0effb] rounded-3xl p-4 shadow-sm flex items-center gap-4 hover:shadow-md transition">
                <div className="w-10 h-10 rounded-2xl bg-white flex items-center justify-center text-[#6366f1] font-bold text-lg shadow-sm">
                  ⟳
                </div>
                <div>
                  <p className="text-[10px] font-bold text-[#6366f1]/80 uppercase tracking-wide">In Progress</p>
                  <p className="text-base font-black text-gray-800 leading-tight">{metrics.inProgress} Tasks</p>
                </div>
              </div>

              {/* Card 3: Tasks ToDo */}
              <div className="bg-[#fef7ea] rounded-3xl p-4 shadow-sm flex items-center gap-4 hover:shadow-md transition">
                <div className="w-10 h-10 rounded-2xl bg-white flex items-center justify-center text-[#f59e0b] font-bold text-lg shadow-sm">
                  📝
                </div>
                <div>
                  <p className="text-[10px] font-bold text-[#f59e0b]/80 uppercase tracking-wide">Total ToDo</p>
                  <p className="text-base font-black text-gray-800 leading-tight">{metrics.total} Tasks</p>
                </div>
              </div>

              {/* Card 4: Incomplete */}
              <div className="bg-[#fdf2f2] rounded-3xl p-4 shadow-sm flex items-center gap-4 hover:shadow-md transition">
                <div className="w-10 h-10 rounded-2xl bg-white flex items-center justify-center text-[#ef4444] font-bold text-lg shadow-sm">
                  ✗
                </div>
                <div>
                  <p className="text-[10px] font-bold text-[#ef4444]/80 uppercase tracking-wide">Incomplete</p>
                  <p className="text-base font-black text-gray-800 leading-tight">{metrics.incomplete} Tasks</p>
                </div>
              </div>

            </div>
          </div>

          {/* Task Board Kanban Layout */}
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="text-base font-black text-gray-800">Task Board ({formattedSelectedDate})</h3>
              <button 
                onClick={() => navigate("/dashboard/workingeod")}
                className="text-xs font-bold text-indigo-600 hover:text-indigo-700"
              >
                See More
              </button>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-4 gap-4 items-start">
              
              {/* Column 1: To Do */}
              <div className="bg-[#f8f9fc] rounded-2xl p-3 space-y-3">
                <div className="flex justify-between items-center px-1">
                  <span className="text-xs font-bold text-gray-500 uppercase tracking-wide">To Do</span>
                  <span className="text-[10px] bg-gray-200 px-2 py-0.5 rounded-full text-gray-600 font-bold">
                    {kanbanColumns.todo.length}
                  </span>
                </div>
                <div className="space-y-2">
                  {kanbanColumns.todo.length === 0 ? (
                    <p className="text-[11px] text-gray-400 text-center py-4 bg-white rounded-xl border border-dashed border-gray-200">No tasks</p>
                  ) : (
                    kanbanColumns.todo.map((t, idx) => (
                      <div key={idx} className="bg-white p-3 rounded-xl border border-gray-100 shadow-sm space-y-2">
                        <span className="text-[9px] font-bold text-amber-500 bg-amber-50 px-1.5 py-0.5 rounded-md">Pending</span>
                        <h4 className="text-xs font-bold text-gray-700 leading-tight mt-1">{t.profile}</h4>
                        <p className="text-[10px] text-gray-400">{t.company}</p>
                      </div>
                    ))
                  )}
                </div>
              </div>

              {/* Column 2: In Progress */}
              <div className="bg-[#f8f9fc] rounded-2xl p-3 space-y-3">
                <div className="flex justify-between items-center px-1">
                  <span className="text-xs font-bold text-gray-500 uppercase tracking-wide">In Progress</span>
                  <span className="text-[10px] bg-indigo-100 px-2 py-0.5 rounded-full text-indigo-600 font-bold">
                    {kanbanColumns.inProgress.length}
                  </span>
                </div>
                <div className="space-y-2">
                  {kanbanColumns.inProgress.length === 0 ? (
                    <p className="text-[11px] text-gray-400 text-center py-4 bg-white rounded-xl border border-dashed border-gray-200">No tasks</p>
                  ) : (
                    kanbanColumns.inProgress.map((t, idx) => (
                      <div key={idx} className="bg-white p-3 rounded-xl border border-gray-100 shadow-sm space-y-2">
                        <span className="text-[9px] font-bold text-indigo-500 bg-indigo-50 px-1.5 py-0.5 rounded-md">In progress</span>
                        <h4 className="text-xs font-bold text-gray-700 leading-tight mt-1">{t.profile}</h4>
                        <p className="text-[10px] text-gray-400">{t.company}</p>
                      </div>
                    ))
                  )}
                </div>
              </div>

              {/* Column 3: Completed */}
              <div className="bg-[#f8f9fc] rounded-2xl p-3 space-y-3">
                <div className="flex justify-between items-center px-1">
                  <span className="text-xs font-bold text-gray-500 uppercase tracking-wide">Completed</span>
                  <span className="text-[10px] bg-green-100 px-2 py-0.5 rounded-full text-green-600 font-bold">
                    {kanbanColumns.completed.length}
                  </span>
                </div>
                <div className="space-y-2">
                  {kanbanColumns.completed.length === 0 ? (
                    <p className="text-[11px] text-gray-400 text-center py-4 bg-white rounded-xl border border-dashed border-gray-200">No tasks</p>
                  ) : (
                    kanbanColumns.completed.map((t, idx) => (
                      <div key={idx} className="bg-white p-3 rounded-xl border border-gray-100 shadow-sm space-y-2">
                        <span className="text-[9px] font-bold text-green-500 bg-green-50 px-1.5 py-0.5 rounded-md">Completed</span>
                        <h4 className="text-xs font-bold text-gray-700 leading-tight mt-1">{t.profile}</h4>
                        <p className="text-[10px] text-gray-400">{t.company}</p>
                      </div>
                    ))
                  )}
                </div>
              </div>

              {/* Column 4: Incomplete */}
              <div className="bg-[#f8f9fc] rounded-2xl p-3 space-y-3">
                <div className="flex justify-between items-center px-1">
                  <span className="text-xs font-bold text-gray-500 uppercase tracking-wide">Incomplete</span>
                  <span className="text-[10px] bg-red-100 px-2 py-0.5 rounded-full text-red-600 font-bold">
                    {kanbanColumns.incomplete.length}
                  </span>
                </div>
                <div className="space-y-2">
                  {kanbanColumns.incomplete.length === 0 ? (
                    <p className="text-[11px] text-gray-400 text-center py-4 bg-white rounded-xl border border-dashed border-gray-200">No tasks</p>
                  ) : (
                    kanbanColumns.incomplete.map((t, idx) => (
                      <div key={idx} className="bg-white p-3 rounded-xl border border-gray-100 shadow-sm space-y-2">
                        <span className="text-[9px] font-bold text-red-500 bg-red-50 px-1.5 py-0.5 rounded-md">Expired</span>
                        <h4 className="text-xs font-bold text-gray-700 leading-tight mt-1">{t.profile}</h4>
                        <p className="text-[10px] text-gray-400">{t.company}</p>
                      </div>
                    ))
                  )}
                </div>
              </div>

            </div>
          </div>

          {/* Real Team Members Widget (Relocated Below Task Board) */}
          <div className="bg-white border border-gray-100 rounded-3xl p-6 shadow-sm space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="text-base font-black text-gray-800">My Team ({displayTeam.length})</h3>
              <button 
                onClick={() => navigate("/dashboard/hierarchycrgchart")}
                className="text-xs font-bold text-indigo-600 hover:text-indigo-700"
              >
                View Hierarchy
              </button>
            </div>

            {displayTeam.length === 0 ? (
              <p className="text-xs text-gray-400 text-center py-4 bg-gray-50/50 rounded-2xl">
                No team members reporting directly to you.
              </p>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                {displayTeam.slice(0, 3).map((member, idx) => (
                  <div key={idx} className="bg-[#f8f9fc] rounded-2xl p-4 text-center border border-gray-50 flex flex-col items-center">
                    <div className={`w-12 h-12 rounded-full ${member.color} text-white flex items-center justify-center font-bold text-lg shadow-sm`}>
                      {member.name[0]}
                    </div>
                    <h4 className="text-sm font-bold text-gray-800 mt-3 leading-tight truncate w-full">{member.name}</h4>
                    <p className="text-xs text-gray-400 mt-1 truncate w-full">{member.designation}</p>
                    <p className="text-[10px] text-gray-500 mt-0.5">{member.office}</p>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Recent Activity Section */}
          <div className="bg-white border border-gray-100 rounded-3xl p-6 shadow-sm space-y-4">
            <h3 className="text-base font-black text-gray-800">Recent Activity</h3>
            <div className="overflow-x-auto no-scrollbar">
              <table className="w-full text-left text-xs font-bold text-gray-500">
                <thead>
                  <tr className="border-b border-gray-50 text-[10px] text-gray-400 uppercase tracking-wider">
                    <th className="py-2.5 pb-4">Activity / Task</th>
                    <th className="py-2.5 pb-4">Owner</th>
                    <th className="py-2.5 pb-4">Date</th>
                    <th className="py-2.5 pb-4">Company</th>
                    <th className="py-2.5 pb-4 text-center">Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50/50">
                  {tasks.slice(0, 3).map((t, idx) => (
                    <tr key={idx} className="hover:bg-gray-50/50">
                      <td className="py-3 text-gray-800 font-bold">{t.profile}</td>
                      <td className="py-3 text-gray-500">{t.employeename}</td>
                      <td className="py-3 text-gray-400">{t.date}</td>
                      <td className="py-3 text-gray-500">{t.company || "-"}</td>
                      <td className="py-3 text-center">
                        <span className={`px-2 py-1 rounded-md text-[9px] font-bold ${
                          t.status === "Completed"
                            ? "text-[#22c55e] bg-[#e8faf0]"
                            : "text-[#f59e0b] bg-[#fff8eb]"
                        }`}>
                          {t.status}
                        </span>
                      </td>
                    </tr>
                  ))}
                  {tasks.length === 0 && (
                    <tr>
                      <td colSpan="5" className="text-center py-6 text-gray-400 italic">No recent activities available.</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

        </div>

        {/* ================================================= */}
        {/* RIGHT COLUMN: SIDE BAR INFO WIDGETS (30% width) */}
        {/* ================================================= */}
        <div className="space-y-6">
          
          {/* Onboarding Candidate Checklist Widget (With View All) */}
          <div className="bg-white border border-gray-100 rounded-3xl p-6 shadow-sm space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="text-base font-black text-gray-800">Onboarding</h3>
              <button 
                onClick={() => navigate("/dashboard/myreport?tab=placementtracker")}
                className="text-xs font-bold text-indigo-600 hover:text-indigo-700"
              >
                View All
              </button>
            </div>

            {/* Micro Tabs */}
            <div className="flex bg-[#f4f6fa] p-1 rounded-xl">
              <button
                onClick={() => setOnboardingTab("new")}
                className={`flex-1 text-center py-1.5 rounded-lg text-xs font-bold transition ${
                  onboardingTab === "new" ? "bg-white text-gray-800 shadow-sm" : "text-gray-400 hover:text-gray-600"
                }`}
              >
                New Candidates
              </button>
              <button
                onClick={() => setOnboardingTab("old")}
                className={`flex-1 text-center py-1.5 rounded-lg text-xs font-bold transition ${
                  onboardingTab === "old" ? "bg-white text-gray-800 shadow-sm" : "text-gray-400 hover:text-gray-600"
                }`}
              >
                Past Joinings
              </button>
            </div>

            {/* List */}
            <div className="space-y-3">
              {displayJoinings.length === 0 ? (
                <p className="text-xs text-gray-400 text-center py-6">No onboarding lists</p>
              ) : (
                displayJoinings.map((join, idx) => (
                  <div key={idx} className="bg-[#f8f9fc] rounded-xl p-3 border border-gray-50 flex items-center justify-between gap-3">
                    <div className="min-w-0">
                      <h4 className="text-xs font-bold text-gray-800 truncate leading-tight">{join.candidate_name}</h4>
                      <p className="text-[10px] text-gray-400 truncate mt-0.5">{join.designation}</p>
                      <p className="text-[10px] text-gray-500 font-semibold truncate leading-none mt-1">{join.company}</p>
                    </div>
                    <span className="text-[10px] text-indigo-600 font-black bg-indigo-50 px-2 py-1 rounded-md whitespace-nowrap">
                      {new Date(join.doj).toLocaleDateString(undefined, { day: '2-digit', month: '2-digit' })}
                    </span>
                  </div>
                ))
              )}
            </div>
          </div>

          {/* Interview Pipeline / Upcoming Interviews Widget */}
          <div className="bg-white border border-gray-100 rounded-3xl p-6 shadow-sm space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="text-base font-black text-gray-800">Interview Pipeline</h3>
              <button 
                onClick={() => navigate("/dashboard/myreport?tab=interview")}
                className="text-xs font-bold text-indigo-600 hover:text-indigo-700"
              >
                View All
              </button>
            </div>

            <div className="space-y-3">
              {upcomingInterviews.length === 0 ? (
                <p className="text-xs text-gray-400 text-center py-6">No upcoming interviews</p>
              ) : (
                upcomingInterviews.map((item, idx) => (
                  <div key={idx} className="bg-[#f8f9fc] rounded-xl p-3 border border-gray-50 flex items-center justify-between gap-3">
                    <div className="min-w-0">
                      <h4 className="text-xs font-bold text-gray-800 truncate leading-tight">{item.candidate_name}</h4>
                      <p className="text-[10px] text-gray-400 truncate mt-0.5">{item.position}</p>
                      <p className="text-[10px] text-gray-500 font-semibold truncate leading-none mt-1">{item.company}</p>
                    </div>
                    <span className="text-[10px] text-indigo-600 font-black bg-indigo-50 px-2 py-1 rounded-md whitespace-nowrap">
                      {normalizeDate(item.interview_date)?.split("-").reverse().slice(0, 2).join("-")}
                    </span>
                  </div>
                ))
              )}
            </div>
          </div>

          {/* Graph pie progress widget */}
          <div className="bg-white border border-gray-100 rounded-3xl p-6 shadow-sm space-y-4 text-center">
            <h3 className="text-base font-black text-gray-800 text-left">Graph / Progress</h3>
            
            <div className="h-44 flex items-center justify-center relative">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={donutData}
                    cx="50%"
                    cy="50%"
                    innerRadius={55}
                    outerRadius={70}
                    startAngle={90}
                    endAngle={-270}
                    paddingAngle={0}
                    dataKey="value"
                  >
                    <Cell fill="#6366f1" />
                    <Cell fill="#f4f6fa" />
                  </Pie>
                </PieChart>
              </ResponsiveContainer>

              {/* Centered label */}
              <div className="absolute inset-0 flex flex-col items-center justify-center">
                <span className="text-2xl font-black text-gray-800 leading-none">
                  {metrics.progressPercent}%
                </span>
                <span className="text-[10px] text-gray-400 font-bold uppercase mt-1 leading-none">
                  Total Progress
                </span>
              </div>
            </div>
          </div>

        </div>

      </div>
    </div>
  );
}